import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { Skeleton } from "../../../Components/Needed";
import { useTrackedForm, useUnsaved } from "../unsaved";

const PaymentTab = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { form, setForm, loadForm, dirty, discard } = useTrackedForm({ publicKey: "", secretKey: "" });
    const [revealSecret, setRevealSecret] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({ method: "GET", url: "/admin/business/site/payment/view" });
        if (response?.success) {
            const data = response?.data?.data || {};
            loadForm({
                publicKey: data?.razorpay?.publicKey || "",
                secretKey: data?.razorpay?.secretKey || "",
            });
        }
        setLoading(false);
    }, [loadForm]);

    useEffect(() => { load(); }, [load]);

    const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const onSave = async () => {
        if (!form.publicKey?.trim() || !form.secretKey?.trim()) {
            toast.error("Both the public key and secret key are required");
            return;
        }
        setSaving(true);
        const response = await Handler({
            method: "patch",
            url: "/admin/business/site/payment/update",
            data: { publicKey: form.publicKey.trim(), secretKey: form.secretKey.trim() },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Payment settings updated");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to update payment settings"));
        }
        setSaving(false);
    };

    useUnsaved("payment", dirty, onSave, discard);

    if (loading) {
        return (
            <div className="settings-card">
                <Skeleton height={24} width="40%" className="mb-3" />
                <Skeleton height={38} className="mb-3" />
                <Skeleton height={38} className="mb-3" />
            </div>
        );
    }

    return (
        <div className="settings-card">
            <h5 className="fw-semibold mb-3">Payment Gateway (Razorpay)</h5>
            <p className="text-muted small mb-4">These keys are used to create and verify booking payments - treat the secret key like a password.</p>

            <div className="row g-4">
                <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="payment-public-key">Public key</label>
                    <input id="payment-public-key" type="text" name="publicKey" className="form-control" value={form.publicKey} onChange={handleChange} autoComplete="off" />
                </div>
                <div className="col-12 col-md-6">
                    <label className="form-label" htmlFor="payment-secret-key">Secret key</label>
                    <div className="input-group">
                        <input
                            id="payment-secret-key"
                            type={revealSecret ? "text" : "password"}
                            name="secretKey"
                            className="form-control"
                            value={form.secretKey}
                            onChange={handleChange}
                            autoComplete="new-password"
                        />
                        <button type="button" className="btn btn-outline-secondary" onClick={() => setRevealSecret((v) => !v)} aria-label={revealSecret ? "Hide secret key" : "Show secret key"}>
                            {revealSecret ? <FaEyeSlash /> : <FaEye />}
                        </button>
                    </div>
                </div>
            </div>

            <div className="settings-card-actions">
                <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
                    {saving ? "Saving..." : "Save changes"}
                </button>
            </div>
        </div>
    );
};

export default PaymentTab;
